import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { MessageSquareText, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * SendWaTemplateDialog — used on TicketDetail (WhatsApp channel tickets) to
 * send a pre-approved Meta WhatsApp Business template to the customer.
 *
 * Props:
 *   open, onOpenChange
 *   ticket — current ticket object (for showing dest phone + appending comment locally)
 *   onSent — callback(ticket) when the comment was appended
 */
export default function SendWaTemplateDialog({ open, onOpenChange, ticket, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [params, setParams] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  // Load templates whenever dialog opens
  useEffect(() => {
    if (!open) return;
    api.get("/wa-templates").then((r) => {
      const approved = (r.data || []).filter((t) => t.status === "approved");
      setTemplates(approved);
      if (approved[0]) setTemplateId(approved[0].id);
    });
  }, [open]);

  const selected = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId]
  );

  // Reset params + preview when selection changes
  useEffect(() => {
    if (!selected) { setParams([]); setPreview(null); return; }
    setParams(Array(selected.param_count || 0).fill(""));
    setPreview(null);
  }, [templateId, selected?.id]);

  // Auto-fill params from ticket fields
  useEffect(() => {
    if (!selected || !ticket || (selected.param_count || 0) === 0) return;
    const defaults = [];
    const fields = [ticket.customer_name, ticket.code, ticket.status, ticket.department_name];
    for (let i = 0; i < selected.param_count; i += 1) {
      defaults[i] = fields[i] || "";
    }
    setParams(defaults);
  }, [selected?.id, ticket?.id]); // eslint-disable-line

  const runPreview = async () => {
    try {
      const { data } = await api.post(`/wa-templates/${templateId}/preview`, { params });
      setPreview(data.rendered);
    } catch (e) { toast.error("Preview failed"); }
  };

  const send = async () => {
    if (!templateId) return toast.error("Pick a template first");
    if (selected && params.length < (selected.param_count || 0)) {
      return toast.error(`Fill all ${selected.param_count} params first`);
    }
    setSending(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/wa-template`, {
        template_id: templateId, params,
      });
      if (data.sent) {
        toast.success("Template sent via WhatsApp");
      } else if (data.skipped_reason === "whatsapp_not_configured") {
        toast.info("WhatsApp credentials not configured — comment logged but no outbound message sent");
      } else {
        toast.error(data.error || "Send failed");
      }
      // Refresh the ticket so the new comment appears
      const { data: tk } = await api.get(`/tickets/${ticket.id}`);
      if (onSent) onSent(tk);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!ticket) return null;
  const recipient = `${ticket.country_code || ""} ${ticket.contact_number || ""}`.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-emerald-600" />
            Send WhatsApp template
          </DialogTitle>
          <DialogDescription className="text-xs">
            Use this to re-engage the customer when the 24-hour service window has closed.
            Only <strong>approved</strong> templates can be sent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600">
            To: <span className="font-mono text-slate-800">{recipient || "—"}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            Ticket: <span className="font-mono text-slate-800">{ticket.code}</span>
          </div>

          {templates.length === 0 ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
              <span>
                No approved templates yet. Ask an admin to add and approve templates in
                <strong> Settings → WhatsApp Templates</strong>.
              </span>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>Template</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="wa-send-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="font-mono">{t.name}</span>
                        <Badge variant="outline" className="ml-2 text-[10px] bg-slate-50">{t.category}</Badge>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(selected?.param_count || 0) > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs">Parameters</Label>
                  {Array.from({ length: selected.param_count }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] font-mono bg-indigo-50 text-indigo-700 border-indigo-200 shrink-0">
                        {`{{${i + 1}}}`}
                      </Badge>
                      <Input
                        value={params[i] || ""}
                        onChange={(e) => {
                          const next = [...params]; next[i] = e.target.value; setParams(next);
                        }}
                        className="text-sm"
                        data-testid={`wa-send-param-${i + 1}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <Button variant="outline" size="sm" onClick={runPreview} data-testid="wa-send-preview-btn">
                  Preview
                </Button>
                {preview && (
                  <span className="text-[10px] text-slate-400">Showing rendered preview ↓</span>
                )}
              </div>

              {preview && (
                <div className="rounded-md border border-emerald-200 bg-[#e7f8c7] p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="wa-send-preview-body">
                  {preview.header && <div className="font-bold text-slate-900 mb-1">{preview.header}</div>}
                  <div className="text-slate-800">{preview.body}</div>
                  {preview.footer && <div className="text-[10px] text-slate-500 mt-2">{preview.footer}</div>}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={send}
            disabled={!templateId || sending || templates.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="wa-send-submit-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sending ? "Sending..." : "Send template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
